import './cellRenderers.css';
import PropTypes from 'prop-types';
import { formatNumber } from '@icp/i18n';
import { ensureDisplayText } from '@icp/utils';
import { ConditionalPropertyPropType } from '../../../propTypes';
import LinkWrapper from '../../../wrappers/LinkWrapper';
import withCurrentData from './withCurrentData';
import { useVariablePattern } from '../../../hooks';

function NumberCellRenderer(props) {
  const {
    value,
    valueFormatted,
    href,
    hrefSelector,
    hrefIsSiteBased,
    target,
    replace,
    suppressBasePath,
    suppressInheritIncludeDeleted,
    formatOptions,
  } = props;

  const currency = useVariablePattern(formatOptions?.currency, false);

  // '[object Object]' 是因为 ag-grid 在 valueFormatted 做了多余的事情，最后判断如果 value 是 array 会强制调用 value.join(', '),
  // 导致即使 colDef 没有提供 valueFormatter 的时候 [{}, {}] 会变成 '[object Object], [object Object]'
  const displayText =
    typeof valueFormatted === 'number' ||
    (typeof valueFormatted === 'string' && !valueFormatted.startsWith('[object Object]'))
      ? valueFormatted
      : NumberCellRenderer.toDisplayText(value, {
          formatOptions: { ...formatOptions, currency },
        });

  if (href || hrefSelector) {
    return (
      <LinkWrapper
        className="table-number-cell"
        componentProps={{
          href,
          hrefSelector,
          hrefIsSiteBased,
          target,
          replace,
          suppressBasePath,
          suppressInheritIncludeDeleted,
          suppressLinkColor: false,
        }}
      >
        {ensureDisplayText(displayText)}
      </LinkWrapper>
    );
  }

  return <span className="table-number-cell">{ensureDisplayText(displayText)}</span>;
}

NumberCellRenderer.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  valueFormatted: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  data: PropTypes.shape({}),
  // href must like '/path/{id}' or '/path/:Id'
  // TODO, support pattern '/path/{id}'
  href: ConditionalPropertyPropType(PropTypes.string),
  hrefSelector: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      matched: ConditionalPropertyPropType(PropTypes.bool),
    }),
  ),
  /**
   * 如果值为 `false` 并且此链接位于 pbc 下，href 将自动拼接上 pbc 的 `token` 作为 `basename`，移动端将自动拼接上 `mobile` 的前缀
   */
  hrefIsSiteBased: PropTypes.bool,
  /**
   * 是否直接链接不通过 react router 的 base path
   */
  suppressBasePath: PropTypes.bool,
  /**
   * 是否禁止自动继承当前页面的 `include_deleted` 属性，此属性一般在当前页面 url 的 search params 里
   */
  suppressInheritIncludeDeleted: PropTypes.bool,
  /**
   * html a 标签的 target 属性
   */
  target: PropTypes.string,
  /**
   * react-router Link 的 replace 属性
   */
  replace: PropTypes.bool,
  /**
   * Intl.NumberFormat的所有属性
   */
  formatOptions: PropTypes.shape({
    // Intl.NumberFormat的属性，支持冒号语法引用表单数据
    currency: PropTypes.string,
  }),
};

NumberCellRenderer.parseValue = (value /* , cellRendererParams */) => {
  if (!value) return value;
  if (Number.isNaN(+value)) return value;
  return +value;
};

NumberCellRenderer.toDisplayText = (originValue, cellRendererParams) => {
  const { formatOptions } = cellRendererParams || {};

  return formatNumber(originValue, formatOptions);
};

// 字符串拼接让被格式化成类似 123,456 Number 可以同时搜索 123456 和 123,456 出来
NumberCellRenderer.getQuickFilterText = (params) => {
  const { value, colDef } = params;
  return `${value} ${NumberCellRenderer.toDisplayText(value, colDef.cellRendererParams)}`;
};

export default withCurrentData(NumberCellRenderer);
